/*
 *
 *  * (C) Copyright 2006-2020 hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * All Rights Reserved.
 *  *
 *  * These materials are unpublished, proprietary, confidential source code of
 *  * hSenid Mobile Solutions (Pvt) Limited and constitute a TRADE SECRET
 *  * of hSenid Mobile Solutions (Pvt) Limited.
 *  *
 *  * hSenid Mobile Solutions (Pvt) Limited retains all title to and intellectual
 *  * property rights in these materials.
 *  *
 *
 */

import produce from "immer";
import { call, put, takeLatest } from "redux-saga/effects";
import { usePortalStateSelector } from "../../rootReducer/actions";
import {
  App,
  AppUser,
  OTPRequestRespPayload,
  requestOTP,
  signOutUser,
  subscribe,
  SubscriptionRequest,
  updateUser,
  verifyOTP,
} from "../../api/apiCalls";
import { AppDetailsPageEvent } from "../Pages/AppDetailsPage";

export enum MainPageEvent {
  RESET = "RESET",
  SHOW_SIGNIN_MODAL = "SHOW_SIGNIN_MODAL",
  HIDE_SIGNIN_MODAL = "HIDE_SIGNIN_MODAL",
  HIDE_VERIFY_OTP_MODAL = "HIDE_VERIFY_OTP_MODAL",
  OTP_REQUEST = "OTP_REQUEST",
  OTP_REQUEST_SUCCESS = "OTP_REQUEST_SUCCESS",
  OTP_REQUEST_FAILURE = "OTP_REQUEST_FAILURE",
  OTP_VERIFY = "OTP_VERIFY",
  OTP_VERIFY_SUCCESS = "OTP_VERIFY_SUCCESS",
  OTP_VERIFY_FAILURE = "OTP_VERIFY_FAILURE",
  OTP_VERIFY_MAX_TRIES_EXCEEDED = "OTP_VERIFY_MAX_TRIES_EXCEEDED",
  SIGN_OUT = "SIGN_OUT",
  SIGN_OUT_SUCCESS = "SIGN_OUT_SUCCESS",
  SHOW_EDIT_PROFILE_MODAL = "SHOW_EDIT_PROFILE_MODAL",
  HIDE_EDIT_PROFILE_MODAL = "HIDE_EDIT_PROFILE_MODAL",
  SHOW_SUBSCRIPTION_MODAL = "SHOW_SUBSCRIPTION_MODAL",
  HIDE_SUBSCRIPTION_MODAL = "HIDE_SUBSCRIPTION_MODAL",
  SHOW_SUBSCRIPTION_RESULT_MODAL = "SHOW_SUBSCRIPTION_RESULT_MODAL",
  HIDE_SUBSCRIPTION_RESULT_MODAL = "HIDE_SUBSCRIPTION_RESULT_MODAL",
  SHOW_UNSUBSCRIPTION_MODAL = "SHOW_UNSUBSCRIPTION_MODAL",
  HIDE_UNSUBSCRIPTION_MODAL = "HIDE_UNSUBSCRIPTION_MODAL",
  UPDATE_USER = "UPDATE_USER",
  UPDATE_USER_SUCCESS = "UPDATE_USER_SUCCESS",
  UPDATE_USER_FAILURE = "UPDATE_USER_FAILURE",
  SHOW_NOTIFICATION = "SHOW_NOTIFICATION",
  HIDE_NOTIFICATION = "HIDE_NOTIFICATION",
  SUBSCRIBE = "SUBSCRIBE",
  SUBSCRIBE_SUCCESS = "SUBSCRIBE_SUCCESS",
  SUBSCRIBE_FAILURE = "SUBSCRIBE_FAILURE",
  UNSUBSCRIBE = "UNSUBSCRIBE",
  UNSUBSCRIBE_SUCCESS = "UNSUBSCRIBE_SUCCESS",
  UNSUBSCRIBE_FAILURE = "UNSUBSCRIBE_FAILURE",
}

export interface ShowSubscriptionModalAction {
  type: MainPageEvent.SHOW_SUBSCRIPTION_MODAL;
  payload: App;
}

export interface HideSubscriptionModalAction {
  type: MainPageEvent.HIDE_SUBSCRIPTION_MODAL;
}

export interface ShowSubscriptionResultModalAction {
  type: MainPageEvent.SHOW_SUBSCRIPTION_RESULT_MODAL;
  payload: App;
}

export interface HideSubscriptionResultModalAction {
  type: MainPageEvent.HIDE_SUBSCRIPTION_RESULT_MODAL;
}

export interface ShowUnsubscriptionModalAction {
  type: MainPageEvent.SHOW_UNSUBSCRIPTION_MODAL;
  payload: App;
}

export interface HideUnsubscriptionModalAction {
  type: MainPageEvent.HIDE_UNSUBSCRIPTION_MODAL;
}

export interface ShowSignInModalAction {
  type: MainPageEvent.SHOW_SIGNIN_MODAL;
}

export interface HideSignInModalAction {
  type: MainPageEvent.HIDE_SIGNIN_MODAL;
}

export interface HideVerifyOTPModalAction {
  type: MainPageEvent.HIDE_VERIFY_OTP_MODAL;
}

export interface OTPRequestAction {
  type: MainPageEvent.OTP_REQUEST;
  msisdn: string;
}

export interface OTPRequestSuccessAction {
  type: MainPageEvent.OTP_REQUEST_SUCCESS;
  result: OTPRequestRespPayload;
}

export interface OTPRequestFailureAction {
  type: MainPageEvent.OTP_REQUEST_FAILURE;
}

export interface VerifyOTPAction {
  type: MainPageEvent.OTP_VERIFY;
  payload: {
    otp: string;
    requestId: string;
    requestCorrelator: string;
  };
  getAppReccommendations(): void;
}

export interface VerifyOTPSuccessAction {
  type: MainPageEvent.OTP_VERIFY_SUCCESS;
  results: {
    token: string;
    appUser: AppUser;
  };
}

export interface VerifyOTPFailureAction {
  type: MainPageEvent.OTP_VERIFY_FAILURE;
}

export interface VerifyOTPMaxTriesExceededAction {
  type: MainPageEvent.OTP_VERIFY_MAX_TRIES_EXCEEDED;
}

export interface SignOutAction {
  type: MainPageEvent.SIGN_OUT;
}

export interface SignOutSuccessAction {
  type: MainPageEvent.SIGN_OUT_SUCCESS;
}

export interface ShowEditProfileModalAction {
  type: MainPageEvent.SHOW_EDIT_PROFILE_MODAL;
}

export interface HideEditProfileModalAction {
  type: MainPageEvent.HIDE_EDIT_PROFILE_MODAL;
}

export interface UpdateUserAction {
  type: MainPageEvent.UPDATE_USER;
  appUser: AppUser;
  token: string;
}

export interface UpdateUserSuccessAction {
  type: MainPageEvent.UPDATE_USER_SUCCESS;
  results: AppUser;
}

export interface UpdateUserFailureAction {
  type: MainPageEvent.UPDATE_USER_FAILURE;
}

export interface ShowNotificationAction {
  type: MainPageEvent.SHOW_NOTIFICATION;
  payload: string;
}

export interface HideNotificationAction {
  type: MainPageEvent.HIDE_NOTIFICATION;
}

export interface SubscribeAction {
  type: MainPageEvent.SUBSCRIBE;
  payload: SubscriptionRequest;
  token: string;
}

export interface SubscribeSuccessAction {
  type: MainPageEvent.SUBSCRIBE_SUCCESS;
}

export interface SubscribeFailureAction {
  type: MainPageEvent.SUBSCRIBE_FAILURE;
  results: string;
}

export interface UnsubscribeAction {
  type: MainPageEvent.UNSUBSCRIBE;
  payload: SubscriptionRequest;
  token: string;
}

export interface UnsubscribeSuccessAction {
  type: MainPageEvent.UNSUBSCRIBE_SUCCESS;
}

export interface UnsubscribeFailureAction {
  type: MainPageEvent.UNSUBSCRIBE_FAILURE;
  results: string;
}

export type MainPageAction =
  | ShowSubscriptionModalAction
  | HideSubscriptionModalAction
  | ShowSubscriptionResultModalAction
  | HideSubscriptionResultModalAction
  | ShowUnsubscriptionModalAction
  | HideUnsubscriptionModalAction
  | ShowSignInModalAction
  | HideSignInModalAction
  | HideVerifyOTPModalAction
  | OTPRequestAction
  | OTPRequestSuccessAction
  | OTPRequestFailureAction
  | VerifyOTPAction
  | VerifyOTPSuccessAction
  | VerifyOTPFailureAction
  | VerifyOTPMaxTriesExceededAction
  | SignOutAction
  | SignOutSuccessAction
  | ShowEditProfileModalAction
  | HideEditProfileModalAction
  | UpdateUserAction
  | UpdateUserSuccessAction
  | UpdateUserFailureAction
  | ShowNotificationAction
  | HideNotificationAction
  | SubscribeAction
  | SubscribeSuccessAction
  | SubscribeFailureAction
  | UnsubscribeAction
  | UnsubscribeSuccessAction
  | UnsubscribeFailureAction;

export interface MainPageData {
  signingIn: boolean;
  showSignInModal: boolean;
  showVerifyOTPModal: boolean;
  showEditProfileModal: boolean;
  showSubscriptionModal: boolean;
  showSubscriptionResultModal: boolean;
  showUnsubscriptionModal: boolean;
  subscriptionApp?: App;
  subscribing: boolean;
  authState: boolean;
  authToken: string | null;
  otpRequestId: string | null;
  otpRequestCorrelator: string | null;
  appUser: AppUser;
  msisdn?: string;
  otp?: string;
  showNotification: boolean;
  notificationMsg: string;
}

const initialState = {
  signingIn: false,
  showSignInModal: false,
  showVerifyOTPModal: false,
  showEditProfileModal: false,
  showSubscriptionModal: false,
  showSubscriptionResultModal: false,
  showUnsubscriptionModal: false,
  subscribing: false,
  showNotification: false,
  notificationMsg: "",
  authState: sessionStorage.getItem("isAuthed") === "true",
  authToken: sessionStorage.getItem("token"),
  otpRequestId: sessionStorage.getItem("otpRequestId"),
  otpRequestCorrelator: sessionStorage.getItem("requestCorrelator"),
  appUser: {
    id: sessionStorage.getItem("userId"),
    msisdn: sessionStorage.getItem("msisdn"),
    firstName: sessionStorage.getItem("firstName"),
    lastName: sessionStorage.getItem("lastName"),
    email: sessionStorage.getItem("email"),
    identity: sessionStorage.getItem("identity"),
    postalAddress: sessionStorage.getItem("postalAddress"),
    displayPicture: sessionStorage.getItem("displayPicture"),
  },
};

/**
 * Reducer
 */

export function mainPageReducer(
  state: MainPageData = initialState,
  action: MainPageAction
) {
  return produce(state, (draft) => {
    switch (action.type) {
      case MainPageEvent.SHOW_NOTIFICATION: {
        draft.showNotification = true;
        draft.notificationMsg = action.payload;
        break;
      }
      case MainPageEvent.HIDE_NOTIFICATION: {
        draft.showNotification = false;
        draft.notificationMsg = "";
        break;
      }
      case MainPageEvent.SHOW_SUBSCRIPTION_MODAL: {
        draft.showSubscriptionModal = true;
        draft.subscriptionApp = action.payload;
        break;
      }
      case MainPageEvent.HIDE_SUBSCRIPTION_MODAL: {
        draft.showSubscriptionModal = false;
        break;
      }
      case MainPageEvent.SHOW_SUBSCRIPTION_RESULT_MODAL: {
        draft.showSubscriptionResultModal = true;
        draft.subscriptionApp = action.payload;
        break;
      }
      case MainPageEvent.HIDE_SUBSCRIPTION_RESULT_MODAL: {
        draft.showSubscriptionResultModal = false;
        break;
      }
      case MainPageEvent.SHOW_UNSUBSCRIPTION_MODAL: {
        draft.showUnsubscriptionModal = true;
        draft.subscriptionApp = action.payload;
        break;
      }
      case MainPageEvent.HIDE_UNSUBSCRIPTION_MODAL: {
        draft.showUnsubscriptionModal = false;
        break;
      }

      case MainPageEvent.SHOW_SIGNIN_MODAL: {
        draft.showSignInModal = true;
        break;
      }
      case MainPageEvent.HIDE_SIGNIN_MODAL: {
        draft.showSignInModal = false;
        break;
      }
      case MainPageEvent.HIDE_VERIFY_OTP_MODAL: {
        draft.showVerifyOTPModal = false;
        break;
      }

      case MainPageEvent.OTP_REQUEST: {
        draft.msisdn = action.msisdn;
        draft.signingIn = true;
        break;
      }
      case MainPageEvent.OTP_REQUEST_SUCCESS: {
        draft.signingIn = false;
        draft.otpRequestId = action.result.requestId;
        draft.otpRequestCorrelator = action.result.requestCorrelator;

        sessionStorage.setItem("otpRequestId", action.result.requestId);
        sessionStorage.setItem(
          "requestCorrelator",
          action.result.requestCorrelator
        );

        draft.showSignInModal = false;
        draft.showVerifyOTPModal = true;

        break;
      }
      case MainPageEvent.OTP_REQUEST_FAILURE: {
        draft.signingIn = false;
        draft.showSignInModal = false;
        break;
      }
      case MainPageEvent.OTP_VERIFY: {
        draft.signingIn = true;
        draft.otp = action.payload.otp;
        break;
      }
      case MainPageEvent.OTP_VERIFY_SUCCESS: {
        draft.signingIn = false;
        draft.authState = true;
        draft.authToken = action.results.token;
        draft.appUser = action.results.appUser;
        draft.showVerifyOTPModal = false;

        sessionStorage.removeItem("requestCorrelator");
        sessionStorage.removeItem("requestCorotpRequestIdrelator");

        sessionStorage.setItem("isAuthed", draft.authState.toString());
        sessionStorage.setItem("token", action.results.token);
        sessionStorage.setItem("userId", action.results.appUser.id || "");
        sessionStorage.setItem("msisdn", action.results.appUser.msisdn || "");
        sessionStorage.setItem(
          "firstName",
          action.results.appUser.firstName || ""
        );
        sessionStorage.setItem(
          "lastName",
          action.results.appUser.lastName || ""
        );
        sessionStorage.setItem("email", action.results.appUser.email || "");
        sessionStorage.setItem(
          "identity",
          action.results.appUser.identity || ""
        );
        sessionStorage.setItem(
          "postalAddress",
          action.results.appUser.postalAddress || ""
        );
        sessionStorage.setItem(
          "displayPicture",
          action.results.appUser.displayPicture || ""
        );
        break;
      }
      case MainPageEvent.OTP_VERIFY_FAILURE: {
        draft.signingIn = false;
        break;
      }
      case MainPageEvent.OTP_VERIFY_MAX_TRIES_EXCEEDED: {
        draft.signingIn = false;
        draft.authState = false;
        draft.showVerifyOTPModal = false;
        draft.showSignInModal = true;
        break;
      }
      case MainPageEvent.SIGN_OUT_SUCCESS: {
        draft.authState = false;
        draft.authToken = null;
        draft.appUser = {
          id: null,
          msisdn: null,
          firstName: null,
          lastName: null,
          email: null,
          identity: null,
          postalAddress: null,
          displayPicture: null,
        };
        sessionStorage.removeItem("isAuthed");
        sessionStorage.removeItem("token");
        sessionStorage.removeItem("userId");
        sessionStorage.removeItem("msisdn");
        sessionStorage.removeItem("firstName");
        sessionStorage.removeItem("lastName");
        sessionStorage.removeItem("email");
        sessionStorage.removeItem("identity");
        sessionStorage.removeItem("postalAddress");
        sessionStorage.removeItem("displayPicture");
        sessionStorage.removeItem("otpRequestId");

        break;
      }
      case MainPageEvent.SHOW_EDIT_PROFILE_MODAL: {
        draft.showEditProfileModal = true;
        break;
      }
      case MainPageEvent.HIDE_EDIT_PROFILE_MODAL: {
        draft.showEditProfileModal = false;
        break;
      }
      case MainPageEvent.UPDATE_USER_SUCCESS: {
        draft.appUser.firstName = action.results.firstName;
        draft.appUser.lastName = action.results.lastName;
        draft.showEditProfileModal = false;
        sessionStorage.setItem("firstName", action.results.firstName || "");
        sessionStorage.setItem("lastName", action.results.lastName || "");
        break;
      }
      case MainPageEvent.UPDATE_USER_FAILURE: {
        draft.showEditProfileModal = false;
        break;
      }
      case MainPageEvent.SUBSCRIBE: {
        draft.subscribing = true;
        break;
      }
      case MainPageEvent.SUBSCRIBE_SUCCESS: {
        draft.subscribing = false;
        draft.showSubscriptionModal = false;
        draft.showSubscriptionResultModal = true;
        break;
      }
      case MainPageEvent.SUBSCRIBE_FAILURE: {
        draft.subscribing = false;
        draft.showSubscriptionModal = false;
        draft.showNotification = true;
        if (action.results === "E1351") {
          draft.notificationMsg = "You have already subscribed to this app!";
        } else {
          draft.notificationMsg = "Oops! Something went wrong";
        }
        break;
      }
      case MainPageEvent.UNSUBSCRIBE: {
        draft.subscribing = true;
        break;
      }
      case MainPageEvent.UNSUBSCRIBE_SUCCESS: {
        draft.subscribing = false;
        draft.showUnsubscriptionModal = false;
        draft.showNotification = true;
        draft.notificationMsg = "Unsubscribed successfully!";
        break;
      }
      case MainPageEvent.UNSUBSCRIBE_FAILURE: {
        draft.subscribing = false;
        draft.showUnsubscriptionModal = false;
        draft.showNotification = true;
        if (action.results === "E1356") {
          draft.notificationMsg = "You have not subscribed to this app!";
        } else {
          draft.notificationMsg = "Oops! Something went wrong";
        }
        break;
      }
    }
  });
}

/**
 * Selector
 */
export function useMainPageDataSelector(): MainPageData {
  return usePortalStateSelector<MainPageData>((ev) => ev.mainPageData);
}

/**
 * Page Saga
 */
export const mainPageSagas = [
  otpRequestSaga,
  otpVerifySaga,
  signOutSaga,
  updateUserSaga,
  subscribeSaga,
  unsubscribeSaga,
];

/**
 * Watcher Sagas
 */

function* otpRequestSaga() {
  yield takeLatest(MainPageEvent.OTP_REQUEST, callRequestOTP);
}

function* otpVerifySaga() {
  yield takeLatest(MainPageEvent.OTP_VERIFY, callVerifyOTP);
}

function* signOutSaga() {
  yield takeLatest(MainPageEvent.SIGN_OUT, callSignOut);
}

function* updateUserSaga() {
  yield takeLatest(MainPageEvent.UPDATE_USER, callUpdateUser);
}

function* subscribeSaga() {
  yield takeLatest(MainPageEvent.SUBSCRIBE, callSubscribe);
}

function* unsubscribeSaga() {
  yield takeLatest(MainPageEvent.UNSUBSCRIBE, callUnsubscribe);
}

/**
 * Handler functions
 */

const arr = ["E1008", "E1009", "E1010", "E1011", "E1012", "E1013"];

function* callRequestOTP(action: OTPRequestAction) {
  try {
    const { statusCode, payload, debugMessage, statusMessage } = yield call(
      requestOTP,
      action.msisdn
    );
    if (statusCode === "S1000") {
      yield put({
        type: MainPageEvent.OTP_REQUEST_SUCCESS,
        result: payload,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: `You will recieve an OTP to ${action.msisdn}`,
      });
    } else {
      if (statusCode === "E1014") {
        yield put({
          type: MainPageEvent.SHOW_NOTIFICATION,
          payload: `User bloked! ${debugMessage}`,
        });
      } else {
        yield put({
          type: MainPageEvent.SHOW_NOTIFICATION,
          payload: "Server error occured!",
        });
      }
      yield put({
        type: MainPageEvent.OTP_REQUEST_FAILURE,
      });
    }
  } catch (e) {
    yield put({
      type: MainPageEvent.OTP_REQUEST_FAILURE,
    });
    yield put({
      type: MainPageEvent.SHOW_NOTIFICATION,
      payload: "Server error occured!",
    });
    console.error(e);
  }
}

function* callVerifyOTP(action: VerifyOTPAction) {
  try {
    const { statusCode, payload, statusMessage, debugMessage } = yield call(
      verifyOTP,
      action.payload
    );

    if (statusCode === "S1000") {
      yield put({
        type: MainPageEvent.OTP_VERIFY_SUCCESS,
        results: payload,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "Log in successfull!",
      });
    } else {
      yield put({
        type: MainPageEvent.OTP_VERIFY_FAILURE,
      });
      if (statusCode === "E1014") {
        yield put({
          type: MainPageEvent.SHOW_NOTIFICATION,
          payload: `User bloked! ${debugMessage}`,
        });
      } else {
        yield put({
          type: MainPageEvent.SHOW_NOTIFICATION,
          payload: "OTP verification failed! Try again",
        });
      }
    }
  } catch (e) {
    yield put({
      type: MainPageEvent.OTP_VERIFY_FAILURE,
    });
    yield put({
      type: MainPageEvent.SHOW_NOTIFICATION,
      payload: "Server error occured!",
    });
    console.error(e);
  }
}

function* callSignOut() {
  try {
    const { payload, statusCode, debugMessage } = yield call(signOutUser);
    if (statusCode === "S1000") {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: payload,
      });
    } else {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: debugMessage || payload,
      });
    }
  } catch (e) {
    yield put({
      type: MainPageEvent.SIGN_OUT_SUCCESS,
    });
    yield put({
      type: MainPageEvent.SHOW_NOTIFICATION,
      payload: "Error! Signed out",
    });
    console.error(e);
  }
}

function* callUpdateUser(action: UpdateUserAction) {
  try {
    const { statusCode, payload, debugMessage } = yield call(
      updateUser,
      action.appUser,
      action.token
    );
    if (statusCode === "S1000") {
      yield put({
        type: MainPageEvent.UPDATE_USER_SUCCESS,
        results: payload,
      });
      yield put({
        type: AppDetailsPageEvent.SHOW_ADD_REVIEW_MODAL,
      });
    } else if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: "JWT expired",
      });
      window.location.reload();
    } else {
      yield put({
        type: MainPageEvent.UPDATE_USER_FAILURE,
      });
      console.error(payload);
    }
  } catch (e) {
    yield put({
      type: MainPageEvent.UPDATE_USER_FAILURE,
    });
    console.error(e);
  }
}

function* callSubscribe(action: SubscribeAction) {
  try {
    const { statusCode, payload, debugMessage } = yield call(
      subscribe,
      action.payload,
      action.token
    );
    if (statusCode === "S1000" && payload?.statusCode === "S1000") {
      yield put({
        type: MainPageEvent.SUBSCRIBE_SUCCESS,
        results: payload,
      });
    } else if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.SUBSCRIBE_FAILURE,
        results: payload?.statusCode,
      });
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: debugMessage || payload,
      });
    } else {
      yield put({
        type: MainPageEvent.SUBSCRIBE_FAILURE,
        results: payload?.statusCode,
      });
      console.error(payload?.statusDetail);
    }
  } catch (e) {
    yield put({
      type: MainPageEvent.SUBSCRIBE_FAILURE,
      results: "E1000",
    });
    console.error(e);
  }
}

function* callUnsubscribe(action: UnsubscribeAction) {
  try {
    const { statusCode, payload, debugMessage } = yield call(
      subscribe,
      action.payload,
      action.token
    );
    if (statusCode === "S1000" && payload?.statusCode === "S1000") {
      yield put({
        type: MainPageEvent.UNSUBSCRIBE_SUCCESS,
        results: payload,
      });
    } else if (arr.includes(statusCode)) {
      yield put({
        type: MainPageEvent.UNSUBSCRIBE_FAILURE,
        results: payload?.statusCode,
      });
      yield put({
        type: MainPageEvent.SIGN_OUT_SUCCESS,
      });
      yield put({
        type: MainPageEvent.SHOW_NOTIFICATION,
        payload: debugMessage || payload,
      });
    } else {
      yield put({
        type: MainPageEvent.UNSUBSCRIBE_FAILURE,
        results: payload?.statusCode,
      });
      console.error(payload?.statusDetail);
    }
  } catch (e) {
    yield put({
      type: MainPageEvent.UNSUBSCRIBE_FAILURE,
      results: "E1000",
    });
    console.error(e);
  }
}
